/**
 * Resuelve la navegación del mapa nuevo a partir de la clave de paso del backend.
 */

import { IdentitiesPath, type IdentitiesContractNavigationState } from "@/screen/Identities/identitiesRoutes";

/**
 * Estado de navegación que consume ContractMapConfiguration al volver al mapa.
 * Se centraliza aquí para evitar el bug de la clave equivocada (savingsPlanId vs savingPlanId):
 * el mapa lee state.savingPlanId / state.savingPlanName (sin la "s" de "savings").
 */
export interface MapNavigationState
{
    savingPlanId: number;
    savingPlanName?: string;
}

export function buildMapNavigationState(savingsPlanId: number, savingsPlanName?: string): MapNavigationState
{
    // savingPlanName se deja undefined si no se conoce: así el mapa usa su propio fallback
    // para el título en lugar de mostrar un nombre vacío.
    return{
        savingPlanId: savingsPlanId,
        savingPlanName: savingsPlanName,
    };
}

export type ContractStepNavigation =
{
    type: "push";
    pathname: string;
    /** Estado de navegación del destino (2.1: contexto de contrato del listado de intervinientes). */
    state?: IdentitiesContractNavigationState;
} | {type: "none"};

export function resolveContractOnboardingStepNavigation(key: string, savingsPlanId: number): ContractStepNavigation
{
    switch(key)
    {
        case "1.1":
            return{
                type: "push",
                pathname: "/savingsPlans/new",
            };
        case "2.1":
            // Ruta única de intervinientes: el contexto de contrato viaja por state.
            return{
                type: "push",
                pathname: IdentitiesPath,
                state: {contractSavingsPlanId: savingsPlanId},
            };
        case "2.2":
            return{
                type: "push",
                pathname: `/contract/${savingsPlanId}/selectIdentities`,
            };
        case "2.2.2":
            return{
                type: "push",
                pathname: `/contract/${savingsPlanId}/suitabilityTest`,
            };
        case "2.2.3":
            return{
                type: "push",
                pathname: `/contract/${savingsPlanId}/authorization`,
            };
        case "2.3":
            return{
                type: "push",
                pathname: `/contract/${savingsPlanId}`,
            };
        // Rama de paso a mayoría de edad: espejo del tronco (2.4.1↔2.1, 2.4.2↔2.2, 2.4.3↔2.2.2,
        // 2.4.4↔2.2.3, 2.4.5↔2.3). Reutiliza las mismas pantallas; el paso vivo lo resuelve
        // useContractStepGuard, que acepta ambas claves.
        case "2.4.1":
            return{
                type: "push",
                pathname: IdentitiesPath,
                state: {contractSavingsPlanId: savingsPlanId},
            };
        case "2.4.2":
            return{
                type: "push",
                pathname: `/contract/${savingsPlanId}/selectIdentities`,
            };
        case "2.4.3":
            return{
                type: "push",
                pathname: `/contract/${savingsPlanId}/suitabilityTest`,
            };
        case "2.4.4":
            return{
                type: "push",
                pathname: `/contract/${savingsPlanId}/authorization`,
            };
        case "2.4.5":
            return{
                type: "push",
                pathname: `/contract/${savingsPlanId}`,
            };
        default:
            // 2.2.1 y 2.4: informativos (el paso a mayoría de edad se valida fuera de la plataforma).
            // 3.x y 4.x: informativos o con acción propia (3.2/3.3 usan advanceStep).
            return { type: "none" };
    }
}

/**
 * Aterrizaje post-alta (SampleProfile u otros creadores de plan).
 * Prioriza currentStep del backend; si no hay paso navegable, usa el mismo
 * mapeo mínimo que holderIsNewlyCreated → 2.1 / 2.2 (un solo diccionario de rutas).
 */
export function resolveContractOnboardingLandingNavigation(currentStep: string | null | undefined, holderIsNewlyCreated: boolean, savingsPlanId: number): ContractStepNavigation
{
    if(currentStep)
    {
        const fromStep = resolveContractOnboardingStepNavigation(currentStep, savingsPlanId);
        if(fromStep.type === "push")
        {
            return fromStep;
        }
    }

    return resolveContractOnboardingStepNavigation(holderIsNewlyCreated ? "2.1" : "2.2", savingsPlanId);
}